import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Select,
  MenuItem,
  IconButton,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Paper,
  Chip,
  ListSubheader,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckIcon from "@mui/icons-material/Check";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import useAutomations from "../../hooks/automationhooks/useAutomations";

///CONTINUE WORKING HERE. NEED TO MAKE DYNAMICTABLE QUERIES WORK

// --- Helpers for parameter substitution (from PipeBuilderQuery) ---
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const toSqlLiteral = (val) => {
  if (val === null || val === undefined) return "NULL";
  if (typeof val === "boolean") return val ? "1" : "0";
  if (typeof val === "number") return String(val);

  const s = String(val).trim();
  if (s !== "" && /^-?\d+(\.\d+)?$/.test(s)) return s;
  return `'${s.replace(/'/g, "''")}'`;
};

const mapDynamicTableDataType = (dataType) => {
  switch (dataType?.toLowerCase()) {
    case "integer":
    case "int":
      return "number";
    case "text":
    case "nvarchar":
    case "varchar":
      return "string";
    case "phone":
      return "string";
    case "bool":
    case "boolean":
      return "boolean";
    case "date":
    case "datetime":
    case "datetime2":
      return "date";
    default:
      return "string";
  }
};

const getOperatorsByDataType = (dataType, fieldName = "") => {
  if (fieldName?.toLowerCase().includes("phone") || dataType === "phone") {
    return [
      { value: "is", label: "Is" },
      { value: "isNot", label: "Is Not" },
      { value: "contains", label: "Contains" },
    ];
  }

  switch (dataType) {
    case "boolean":
      return [
        { value: "is", label: "Is" },
        { value: "isNot", label: "Is Not" },
      ];
    case "string":
      return [
        { value: "is", label: "Is" },
        { value: "isNot", label: "Is Not" },
        { value: "contains", label: "Contains" },
        { value: "startsWith", label: "Starts With" },
        { value: "endsWith", label: "Ends With" },
      ];
    case "number":
      return [
        { value: "is", label: "Equals" },
        { value: "isNot", label: "Does Not Equal" },
        { value: "greaterThan", label: "Greater Than" },
        { value: "lessThan", label: "Less Than" },
        { value: "between", label: "Between" },
      ];
    case "date":
      return [
        { value: "is", label: "Is" },
        { value: "isAfter", label: "Is After" },
        { value: "isBefore", label: "Is Before" },
        { value: "isBetween", label: "Is Between" },
      ];
    default:
      return [];
  }
};

const mapTableFieldToDatabase = (table, field) => {
  const tableNameMap = {
    lead: "CRM_UserLeads",
    contact: "Contacts",
    campaign: "Campaigns",
    visit: "C_Visits",
    engagement: "Engagements",
  };

  const tableName = tableNameMap[table] || table;
  const columnName =
    field && field !== "null"
      ? field.charAt(0).toUpperCase() + field.slice(1)
      : null;

  return { tableName, columnName };
};

const buildConditionClause = (
  columnName,
  operator,
  paramIndex,
  isRange = false
) => {
  const op = operator?.toLowerCase();
  const param1 = `@param${paramIndex}`;
  const param2 = isRange ? `@param${paramIndex + 1}` : null;

  switch (op) {
    case "is":
      return `${columnName} = ${param1}`;
    case "isnot":
      return `${columnName} != ${param1}`;
    case "contains":
      return `${columnName} LIKE '%' + ${param1} + '%'`;
    case "startswith":
      return `${columnName} LIKE ${param1} + '%'`;
    case "endswith":
      return `${columnName} LIKE '%' + ${param1}`;
    case "greaterthan":
      return `${columnName} > ${param1}`;
    case "lessthan":
      return `${columnName} < ${param1}`;
    case "isafter":
      return `${columnName} > ${param1}`;
    case "isbefore":
      return `${columnName} < ${param1}`;
    case "between":
    case "isbetween":
      return param2 ? `${columnName} BETWEEN ${param1} AND ${param2}` : "";
    default:
      return "";
  }
};

// UPDATED: buildSqlQuery with custom join support
const buildSqlQuery = (
  conditions,
  brandId = "@BrandId",
  dynamicTables = [],
  joinConfigs = {} // NEW PARAMETER
) => {
  if (!conditions || conditions.length === 0) {
    return "";
  }

  const andConditions = conditions.filter((c) => c.logicalOperator === "AND");
  const orConditions = conditions.filter((c) => c.logicalOperator === "OR");

  const requiredTables = new Set();
  const conditionsByTable = {};

  [...andConditions, ...orConditions].forEach((condition) => {
    let tableName,
      columnName,
      leadsColumn = "Phone", // Default
      dynamicColumn,
      isDynamic = false;

    if (condition.table?.startsWith("dynamic_")) {
      const actualTableName = condition.table.replace("dynamic_", "");
      const dynamicTable = dynamicTables.find(
        (dt) => dt.TableName === actualTableName
      );
      if (dynamicTable) {
        tableName = dynamicTable.TableName;
        columnName = condition.field;
        isDynamic = true;

        // NEW: Check if custom join config exists for this table
        const customJoin = joinConfigs[actualTableName];
        if (customJoin) {
          leadsColumn = customJoin.leadsField;
          dynamicColumn = customJoin.dynamicField;
        } else {
          dynamicColumn = dynamicTable.PhoneColumnName;
        }
      }
    } else {
      const mapping = mapTableFieldToDatabase(condition.table, condition.field);
      tableName = mapping.tableName;
      columnName = mapping.columnName;
    }

    if (tableName && columnName) {
      requiredTables.add(tableName);
      if (!conditionsByTable[tableName]) {
        conditionsByTable[tableName] = {
          and: [],
          or: [],
          isDynamic,
          leadsColumn, // NEW
          dynamicColumn, // NEW
        };
      }
      const key = condition.logicalOperator === "AND" ? "and" : "or";
      conditionsByTable[tableName][key].push({
        ...condition,
        mappedColumn: columnName,
      });
    }
  });

  let query = "SELECT l.Id\n";
  query += "FROM CRM_UserLeads AS l\n";
  query += `WHERE l.BrandId = ${brandId}\n`;

  let paramIndex = 0;

  const andFragments = [];

  Object.entries(conditionsByTable).forEach(([tableName, tableData]) => {
    if (!tableData.and?.length) return;

    if (tableData.isDynamic) {
      const tableConditions = tableData.and
        .map((c) => {
          const isRange = c.operator?.toLowerCase().includes("between");
          const clause = buildConditionClause(
            `dt.[${c.mappedColumn}]`,
            c.operator,
            paramIndex,
            isRange
          );
          paramIndex += isRange ? 2 : 1;
          return clause;
        })
        .filter(Boolean);

      if (tableConditions.length > 0) {
        // UPDATED: Use custom join columns
        const existsClause = `EXISTS (\n    SELECT 1 FROM [${tableName}] AS dt\n    WHERE dt.BrandId = ${brandId} AND dt.[${
          tableData.dynamicColumn
        }] = l.[${tableData.leadsColumn}] AND (${tableConditions.join(
          " AND "
        )})\n)`;
        andFragments.push(existsClause);
      }
    } else {
      if (tableName === "CRM_UserLeads") {
        const leadsConditions = tableData.and
          .map((c) => {
            const isRange = c.operator?.toLowerCase().includes("between");
            const clause = buildConditionClause(
              `l.[${c.mappedColumn}]`,
              c.operator,
              paramIndex,
              isRange
            );
            paramIndex += isRange ? 2 : 1;
            return clause;
          })
          .filter(Boolean);

        if (leadsConditions.length > 0) {
          andFragments.push(`(${leadsConditions.join(" AND ")})`);
        }
      } else {
        const tableConditions = tableData.and
          .map((c) => {
            const isRange = c.operator?.toLowerCase().includes("between");
            const clause = buildConditionClause(
              `t.[${c.mappedColumn}]`,
              c.operator,
              paramIndex,
              isRange
            );
            paramIndex += isRange ? 2 : 1;
            return clause;
          })
          .filter(Boolean);

        if (tableConditions.length > 0) {
          let existsClause = `EXISTS (\n    SELECT 1 FROM ${tableName} AS t\n    WHERE `;

          if (tableName === "Campaigns") {
            existsClause += `t.GuidId = l.CampaignId AND t.BrandId = ${brandId}`;
          } else if (tableName === "Visits" || tableName === "C_Visits") {
            existsClause += `t.VisitorGuid = l.VisitorGuid AND t.BrandId = ${brandId}`;
          } else if (tableName === "Engagements") {
            existsClause += `t.VisitorGuid = l.VisitorGuid AND t.BrandId = ${brandId}`;
          }

          existsClause += ` AND (${tableConditions.join(" AND ")})\n)`;
          andFragments.push(existsClause);
        }
      }
    }
  });

  if (andFragments.length > 0) {
    query += `  AND ${andFragments.join("\n  AND ")}\n`;
  }

  // Handle OR conditions similarly
  const orFragments = [];

  Object.entries(conditionsByTable).forEach(([tableName, tableData]) => {
    if (!tableData.or?.length) return;

    if (tableData.isDynamic) {
      const tableConditions = tableData.or
        .map((c) => {
          const isRange = c.operator?.toLowerCase().includes("between");
          const clause = buildConditionClause(
            `dt.[${c.mappedColumn}]`,
            c.operator,
            paramIndex,
            isRange
          );
          paramIndex += isRange ? 2 : 1;
          return clause;
        })
        .filter(Boolean);

      if (tableConditions.length > 0) {
        // UPDATED: Use custom join columns
        const existsClause = `EXISTS (\n    SELECT 1 FROM [${tableName}] AS dt\n    WHERE dt.BrandId = ${brandId} AND dt.[${
          tableData.dynamicColumn
        }] = l.[${tableData.leadsColumn}] AND (${tableConditions.join(
          " OR "
        )})\n)`;
        orFragments.push(existsClause);
      }
    } else {
      if (tableName === "CRM_UserLeads") {
        const leadsConditions = tableData.or
          .map((c) => {
            const isRange = c.operator?.toLowerCase().includes("between");
            const clause = buildConditionClause(
              `l.[${c.mappedColumn}]`,
              c.operator,
              paramIndex,
              isRange
            );
            paramIndex += isRange ? 2 : 1;
            return clause;
          })
          .filter(Boolean);

        if (leadsConditions.length > 0) {
          orFragments.push(`(${leadsConditions.join(" OR ")})`);
        }
      }
    }
  });

  if (orFragments.length > 0) {
    query += `  AND (${orFragments.join(" OR ")})\n`;
  }

  return query;
};

// Serialization helpers
const serializeDateRange = (r) =>
  r?.start && r?.end ? `${r.start}|${r.end}` : "";

const deserializeDateRange = (str) => {
  const [start = "", end = ""] = (str || "").split("|");
  return { start, end };
};

const serializeNumberRange = (r) =>
  r && r.min !== "" && r.max !== "" && r.min != null && r.max != null
    ? `${r.min}|${r.max}`
    : "";

const deserializeNumberRange = (str) => {
  if (typeof str !== "string") return { min: "", max: "" };
  const [min = "", max = ""] = str.split("|");
  return {
    min: min === "" ? "" : Number(min),
    max: max === "" ? "" : Number(max),
  };
};

// Field renderer component
const renderField = (fieldConfig, value, onChange, isMainCondition = false) => {
  const labelColor = isMainCondition
    ? { color: "white" }
    : { color: "inherit" };

  switch (fieldConfig.type) {
    case "select":
      return (
        <FormControl size="small" sx={{ minWidth: 150, width: 150 }}>
          <InputLabel sx={labelColor}>{fieldConfig.label}</InputLabel>
          <Select
            size="small"
            sx={{
              minWidth: 150,
              width: 150,
              "& .MuiSelect-select": { py: 0.75 },
            }}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          >
            {fieldConfig.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case "radio":
      return (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5, fontSize: "0.875rem" }}>
            {fieldConfig.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 0,
              height: "36px",
            }}
          >
            {fieldConfig.options?.map((op, index) => (
              <Button
                key={op.value}
                variant={value === op.value ? "contained" : "outlined"}
                onClick={() => onChange(op.value)}
                size="small"
                sx={{
                  flex: 1,
                  height: "36px",
                  minHeight: "36px",
                  borderRadius: 0,
                  fontSize: "0.8125rem",
                  py: 0.5,
                  borderRight:
                    index === fieldConfig.options.length - 1
                      ? undefined
                      : "none",
                  "&:not(:last-child)": {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderRightColor: "transparent",
                  },
                  "&:not(:first-child)": {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    marginLeft: "-1px",
                  },
                }}
              >
                {op.label}
              </Button>
            ))}
          </Box>
        </Box>
      );

    case "text":
      return (
        <TextField
          size="small"
          label={fieldConfig.label}
          InputLabelProps={{ sx: labelColor }}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          required={fieldConfig.required}
          sx={{
            minWidth: 150,
            width: 150,
            "& .MuiInputBase-input": { py: 0.75 },
          }}
        />
      );

    case "datetime":
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label={fieldConfig.label}
            value={value ? dayjs(value) : null}
            onChange={(newValue) =>
              onChange(newValue ? newValue.toISOString() : "")
            }
            slotProps={{
              textField: {
                size: "small",
                sx: {
                  minWidth: 150,
                  width: 150,
                  "& .MuiInputLabel-root": labelColor,
                  "& .MuiInputBase-input": { py: 0.75 },
                },
              },
              popper: {
                placement: "auto",
                modifiers: [
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      boundary: "viewport",
                      padding: 8,
                    },
                  },
                  {
                    name: "flip",
                    enabled: true,
                    options: {
                      fallbackPlacements: ["top", "bottom", "left", "right"],
                    },
                  },
                ],
              },
            }}
          />
        </LocalizationProvider>
      );

    case "daterange":
      return (
        <SingleRangeField
          label={fieldConfig.label || "Date range"}
          labelColor={labelColor}
          value={deserializeDateRange(value)}
          onChange={(range) => onChange(serializeDateRange(range))}
        />
      );

    case "numberRange":
      return (
        <SingleNumberRangeField
          label={fieldConfig.label || "Number range"}
          labelColor={labelColor}
          value={deserializeNumberRange(value)}
          onChange={(range) => onChange(serializeNumberRange(range))}
          settings={fieldConfig.settings}
        />
      );

    default:
      return <Typography>Unknown field type: {fieldConfig.type}</Typography>;
  }
};

// Single Range Field Component
function SingleRangeField({ label, labelColor, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [start, setStart] = React.useState(
    value?.start ? dayjs(value.start) : null
  );
  const [end, setEnd] = React.useState(value?.end ? dayjs(value.end) : null);

  const invalid = Boolean(start && end && end.isBefore(start));
  const display =
    start && end
      ? `${start.format("DD/MM/YYYY hh:mm A")} - ${end.format(
          "DD/MM/YYYY hh:mm A"
        )}`
      : "";

  return (
    <>
      <TextField
        label={label}
        value={display}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setOpen(true);
        }}
        readOnly
        size="small"
        sx={{
          minWidth: 300,
          "& .MuiInputLabel-root": labelColor,
          "& .MuiInputBase-input": { py: 0.75 },
        }}
        error={invalid}
        helperText={invalid ? "End must be after Start" : ""}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ mt: 1 }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
            "& .MuiInputLabel-root": labelColor,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Start"
              value={start}
              onChange={(v) => {
                setStart(v);
                const nextEnd = end && v && end.isBefore(v) ? v : end;
                if (nextEnd !== end) setEnd(nextEnd);
                onChange({
                  start: v ? v.toISOString() : "",
                  end: nextEnd ? nextEnd.toISOString() : "",
                });
              }}
              views={["year", "month", "day", "hours", "minutes"]}
              ampm={true}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 150,
                    width: 150,
                    "& .MuiInputLabel-root": labelColor,
                    "& .MuiInputBase-input": { py: 0.75 },
                  },
                },
                popper: {
                  placement: "auto",
                  modifiers: [
                    {
                      name: "preventOverflow",
                      enabled: true,
                      options: {
                        boundary: "viewport",
                        padding: 8,
                      },
                    },
                    {
                      name: "flip",
                      enabled: true,
                      options: {
                        fallbackPlacements: ["top", "bottom", "left", "right"],
                      },
                    },
                  ],
                },
              }}
              minutesStep={5}
              maxDateTime={end || undefined}
            />
            <DateTimePicker
              label="End"
              value={end}
              onChange={(v) => {
                setEnd(v);
                onChange({
                  start: start ? start.toISOString() : "",
                  end: v ? v.toISOString() : "",
                });
              }}
              views={["year", "month", "day", "hours", "minutes"]}
              ampm={true}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 150,
                    width: 150,
                    "& .MuiInputLabel-root": labelColor,
                    "& .MuiInputBase-input": { py: 0.75 },
                  },
                },
                popper: {
                  placement: "auto",
                  modifiers: [
                    {
                      name: "preventOverflow",
                      enabled: true,
                      options: {
                        boundary: "viewport",
                        padding: 8,
                      },
                    },
                    {
                      name: "flip",
                      enabled: true,
                      options: {
                        fallbackPlacements: ["top", "bottom", "left", "right"],
                      },
                    },
                  ],
                },
              }}
              minDateTime={start || undefined}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
}

// Single Number Range Field Component
function SingleNumberRangeField({
  label,
  labelColor,
  value,
  onChange,
  settings,
}) {
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [min, setMin] = React.useState(value?.min ?? "");
  const [max, setMax] = React.useState(value?.max ?? "");

  const invalid =
    min !== "" &&
    max !== "" &&
    !Number.isNaN(Number(min)) &&
    !Number.isNaN(Number(max))
      ? Number(max) < Number(min)
      : false;

  const display = min !== "" && max !== "" ? `${min} - ${max}` : "";

  const pushChange = (nextMin, nextMax) => {
    onChange({
      min: nextMin === "" ? "" : Number(nextMin),
      max: nextMax === "" ? "" : Number(nextMax),
    });
  };

  return (
    <>
      <TextField
        label={label}
        value={display}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setOpen(true);
        }}
        readOnly
        size="small"
        sx={{
          minWidth: 300,
          "& .MuiInputLabel-root": labelColor,
          "& .MuiInputBase-input": { py: 0.75 },
        }}
        error={invalid}
        helperText={invalid ? "Max must be greater than Min" : ""}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ mt: 1 }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            label="Min"
            type="number"
            size="small"
            value={min}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Number(e.target.value);
              setMin(v);
              pushChange(v, max);
            }}
            inputProps={{
              min: settings?.min ?? undefined,
              max: settings?.max ?? undefined,
            }}
            error={invalid}
            InputLabelProps={{ sx: labelColor }}
            sx={{
              width: 120,
              "& .MuiInputBase-input": { py: 0.75 },
            }}
          />
          <Typography sx={{ color: invalid ? "error.main" : "inherit" }}>
            —
          </Typography>
          <TextField
            label="Max"
            type="number"
            size="small"
            value={max}
            onChange={(e) => {
              const v = e.target.value === "" ? "" : Number(e.target.value);
              setMax(v);
              pushChange(min, v);
            }}
            inputProps={{
              min: settings?.min ?? undefined,
              max: settings?.max ?? undefined,
            }}
            error={invalid}
            InputLabelProps={{ sx: labelColor }}
            sx={{
              width: 120,
              "& .MuiInputBase-input": { py: 0.75 },
            }}
          />
        </Box>
      </Popover>
    </>
  );
}

const ConditionRow = ({
  condition,
  onUpdate,
  onDelete,
  enhancedQueryTypes,
  dynamicJoinConfigs,
  onJoinConfigChange,
  leadsJoinableFields,
}) => {
  const [tempLeadsField, setTempLeadsField] = useState("");
  const [tempDynamicField, setTempDynamicField] = useState("");
  const [joinConfigAnchor, setJoinConfigAnchor] = useState(null);
  const [selectedDynamicTable, setSelectedDynamicTable] = useState(null);

  const handleJoinIconClick = (event, tableData) => {
    event.stopPropagation();
    setSelectedDynamicTable(tableData);
    setJoinConfigAnchor(event.currentTarget);

    const tableName = tableData.TableName;
    const existingConfig = dynamicJoinConfigs[tableName];
    setTempLeadsField(existingConfig?.leadsField || "Phone");
    setTempDynamicField(
      existingConfig?.dynamicField || tableData.PhoneColumnName
    );
  };

  const handleCloseJoinConfig = () => {
    setJoinConfigAnchor(null);
    setSelectedDynamicTable(null);
  };

  const handleApplyJoinConfig = () => {
    if (selectedDynamicTable && tempLeadsField && tempDynamicField) {
      onJoinConfigChange({
        tableName: selectedDynamicTable.TableName,
        leadsField: tempLeadsField,
        dynamicField: tempDynamicField,
      });
      handleCloseJoinConfig();
    }
  };

  const handleResetJoinConfig = () => {
    if (selectedDynamicTable) {
      setTempLeadsField("Phone");
      setTempDynamicField(selectedDynamicTable.PhoneColumnName);
      onJoinConfigChange({
        tableName: selectedDynamicTable.TableName,
        leadsField: "Phone",
        dynamicField: selectedDynamicTable.PhoneColumnName,
      });
      handleCloseJoinConfig();
    }
  };

  const getTableOptions = () => {
    const staticTables = Object.entries(enhancedQueryTypes.tables || {}).map(
      ([key, table]) => ({
        value: key,
        label: table.label,
        isStatic: true,
      })
    );

    const dynamicTables = (enhancedQueryTypes.dynamicTables || []).map(
      (table) => ({
        value: `dynamic_${table.TableName}`,
        label: table.DisplayName,
        isStatic: false,
        tableData: table,
      })
    );
    return { staticTables, dynamicTables };
  };

  const getJoinConfigText = (tableData) => {
    const config = dynamicJoinConfigs[tableData.TableName];
    if (!config) {
      return `Phone ↔ ${tableData.PhoneColumnName}`;
    }

    const isDefault =
      config.leadsField === "Phone" &&
      config.dynamicField === tableData.PhoneColumnName;

    return `${config.leadsField} ↔ ${config.dynamicField}${
      isDefault ? " (Default)" : ""
    }`;
  };

  const mapDynamicTableDataType = (dataType) => {
    switch (dataType?.toLowerCase()) {
      case "integer":
      case "int":
        return "number";
      case "text":
      case "nvarchar":
      case "varchar":
        return "string";
      case "phone":
        return "string";
      case "bool":
      case "boolean":
        return "boolean";
      case "date":
      case "datetime":
      case "datetime2":
        return "date";
      default:
        return "string";
    }
  };

  const getFieldOptions = (tableName) => {
    if (tableName?.startsWith("dynamic_")) {
      const actualTableName = tableName.replace("dynamic_", "");
      const dynamicTable = enhancedQueryTypes.dynamicTables?.find(
        (table) => table.TableName === actualTableName
      );
      return (
        dynamicTable?.Fields?.map((field) => ({
          value: field.ColumnName,
          label: field.ColumnName,
          dataType: mapDynamicTableDataType(field.DataType),
          originalDataType: field.DataType,
        })) || []
      );
    }

    if (enhancedQueryTypes.tables && enhancedQueryTypes.tables[tableName]) {
      return Object.entries(enhancedQueryTypes.tables[tableName].fields).map(
        ([key, field]) => ({
          value: key,
          label: field.label,
          dataType: field.dataType,
        })
      );
    }
    return [];
  };

  const getOperatorsByDataType = (dataType, fieldName = "") => {
    if (fieldName?.toLowerCase().includes("phone") || dataType === "phone") {
      return [
        { value: "is", label: "Is" },
        { value: "isNot", label: "Is Not" },
        { value: "contains", label: "Contains" },
      ];
    }

    switch (dataType) {
      case "boolean":
        return [
          { value: "is", label: "Is" },
          { value: "isNot", label: "Is Not" },
        ];
      case "string":
        return [
          { value: "is", label: "Is" },
          { value: "isNot", label: "Is Not" },
          { value: "contains", label: "Contains" },
          { value: "startsWith", label: "Starts With" },
          { value: "endsWith", label: "Ends With" },
        ];
      case "number":
        return [
          { value: "is", label: "Equals" },
          { value: "isNot", label: "Does Not Equal" },
          { value: "greaterThan", label: "Greater Than" },
          { value: "lessThan", label: "Less Than" },
          { value: "between", label: "Between" },
        ];
      case "date":
        return [
          { value: "is", label: "Is" },
          { value: "isAfter", label: "Is After" },
          { value: "isBefore", label: "Is Before" },
          { value: "isBetween", label: "Is Between" },
        ];
      default:
        return [];
    }
  };

  const getOperatorOptions = (tableName, fieldName) => {
    const fieldOptions = getFieldOptions(tableName);
    const field = fieldOptions.find((f) => f.value === fieldName);
    if (field) return getOperatorsByDataType(field.dataType, fieldName);

    if (
      enhancedQueryTypes.tables &&
      enhancedQueryTypes.tables[tableName] &&
      enhancedQueryTypes.tables[tableName].fields[fieldName]
    ) {
      const field = enhancedQueryTypes.tables[tableName].fields[fieldName];
      const dataType = field.dataType;

      if (
        enhancedQueryTypes.operatorsByType &&
        enhancedQueryTypes.operatorsByType[dataType]
      ) {
        return enhancedQueryTypes.operatorsByType[dataType];
      }

      if (field.operators) {
        return field.operators.map((op) => ({ value: op, label: op }));
      }
    }
    return [];
  };

  const getValueFieldConfig = (tableName, fieldName, operator) => {
    const fieldOptions = getFieldOptions(tableName);
    const field = fieldOptions.find((f) => f.value === fieldName);

    if (field) {
      if (field.dataType === "boolean") {
        return {
          type: "radio",
          label: "Value",
          options: [
            { value: "true", label: "True" },
            { value: "false", label: "False" },
          ],
        };
      }

      switch (field.dataType) {
        case "string":
          return { type: "text", label: "Value" };
        case "number":
          if (operator?.toLowerCase() === "between") {
            return { type: "numberRange", label: "Range" };
          }
          return { type: "text", label: "Value" };
        case "date":
          if (operator?.toLowerCase() === "isbetween") {
            return { type: "daterange", label: "Date Range" };
          }
          return { type: "datetime", label: "Date" };
        default:
          return { type: "text", label: "Value" };
      }
    }

    if (
      enhancedQueryTypes.tables &&
      enhancedQueryTypes.tables[tableName] &&
      enhancedQueryTypes.tables[tableName].fields[fieldName]
    ) {
      const field = enhancedQueryTypes.tables[tableName].fields[fieldName];
      if (field.valueField) return field.valueField;
      if (field.valueOptions) {
        return { type: "select", label: "Value", options: field.valueOptions };
      }
      return { type: "text", label: "Value" };
    }
    return null;
  };

  const { staticTables, dynamicTables } = getTableOptions();

  const renderTableSelect = () => (
    <>
      <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
        <InputLabel sx={{ fontSize: "0.875rem" }}>Table</InputLabel>
        <Select
          size="small"
          value={condition.table || ""}
          onChange={(e) =>
            onUpdate({
              ...condition,
              table: e.target.value,
              field: "",
              operator: "",
              value: "",
            })
          }
          renderValue={(selected) => {
            const allTables = [...staticTables, ...dynamicTables];
            const selectedTable = allTables.find((t) => t.value === selected);
            return selectedTable ? selectedTable.label : "";
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxHeight: 400 },
            },
          }}
          sx={{
            minWidth: 0,
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiSelect-select": {
              py: 0.5,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            },
          }}
        >
          {staticTables.length > 0 && [
            <ListSubheader key="static-header">System Tables</ListSubheader>,
            ...staticTables.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            )),
          ]}

          {dynamicTables.length > 0 && [
            <ListSubheader key="dynamic-header">Dynamic Tables</ListSubheader>,
            ...dynamicTables.flatMap((option) => {
              return [
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>{option.label}</Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleJoinIconClick(e, option.tableData)}
                    sx={{
                      ml: 1,
                      p: 0.5,
                      color: "primary.main",
                    }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </MenuItem>,

                <MenuItem
                  key={`${option.value}-status`}
                  disabled
                  sx={{
                    px: 3,
                    py: 0.5,
                    fontSize: "0.7rem",
                    color: "primary.main",
                    fontStyle: "italic",
                    minHeight: "auto",
                    opacity: 1,
                    cursor: "default",
                    "&.Mui-disabled": {
                      opacity: 1,
                    },
                  }}
                >
                  └─ Join: {getJoinConfigText(option.tableData)}
                </MenuItem>,
              ];
            }),
          ]}
        </Select>
      </FormControl>

      <Dialog
        open={Boolean(joinConfigAnchor)}
        onClose={handleCloseJoinConfig}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Configure Join for {selectedDynamicTable?.DisplayName}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select which columns to join between CRM_UserLeads and this dynamic
            table
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>CRM_UserLeads Field</InputLabel>
              <Select
                value={tempLeadsField}
                label="CRM_UserLeads Field"
                onChange={(e) => setTempLeadsField(e.target.value)}
              >
                {leadsJoinableFields.map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography
              align="center"
              color="text.secondary"
              sx={{ fontWeight: "bold", fontSize: "1.2rem" }}
            >
              ⟷
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>{selectedDynamicTable?.DisplayName} Field</InputLabel>
              <Select
                value={tempDynamicField}
                label={`${selectedDynamicTable?.DisplayName} Field`}
                onChange={(e) => setTempDynamicField(e.target.value)}
              >
                {selectedDynamicTable?.Fields.map((field) => (
                  <MenuItem key={field.ColumnName} value={field.ColumnName}>
                    {field.ColumnName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleResetJoinConfig}>
            Reset to Default
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyJoinConfig}
            disabled={!tempLeadsField || !tempDynamicField}
            startIcon={<CheckIcon />}
          >
            Apply
          </Button>
          <Button variant="outlined" onClick={handleCloseJoinConfig}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const segmentSx = {
    px: 0.5,
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    bgcolor: "transparent",
    overflow: "hidden",
    "& .MuiOutlinedInput-notchedOutline": { border: "none !important" },
    "& .MuiInputBase-root": {
      borderRadius: 0,
      backgroundColor: "transparent",
      minWidth: 0,
      width: "100%",
    },
    "& .MuiSelect-select": {
      py: 0.25,
      fontSize: "0.85rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    "& .MuiInputBase-input": {
      bgcolor: "transparent",
      py: 0.75,
      fontSize: "0.85rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      fontSize: "0.8rem",
    },
    "& .MuiFormControl-root": {
      minWidth: 0,
      width: "100%",
    },
  };

  const COLS = {
    table: "22%",
    field: "22%",
    operator: "18%",
    value: "30%",
    delete: "8%",
  };

  const ROW_WIDTH_PX = 680;

  return (
    <Box
      sx={{
        mb: 0,
        width: ROW_WIDTH_PX,
        flex: "0 0 auto",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          borderRadius: 2,
          overflow: "hidden",
          minHeight: 50,
          width: "100%",
          maxWidth: "100%",
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
          boxSizing: "border-box",
          mb: 0,
        }}
      >
        <Box sx={{ ...segmentSx, flex: `0 0 ${COLS.table}` }}>
          {renderTableSelect()}
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ bgcolor: "divider", width: "1px" }}
        />

        <Box sx={{ ...segmentSx, flex: `0 0 ${COLS.field}` }}>
          {condition.table ? (
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: "0.875rem" }}>Field</InputLabel>
              <Select
                size="small"
                value={condition.field || ""}
                onChange={(e) =>
                  onUpdate({
                    ...condition,
                    field: e.target.value,
                    operator: "",
                    value: "",
                  })
                }
                sx={{
                  minWidth: 0,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-select": {
                    py: 0.5,
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  },
                }}
              >
                {getFieldOptions(condition.table).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl size="small" disabled fullWidth>
              <InputLabel sx={{ color: "text.disabled" }}>Field</InputLabel>
              <Select
                size="small"
                value=""
                sx={{
                  bgcolor: "transparent",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value="">Select table first</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ bgcolor: "divider", width: "1px" }}
        />

        <Box sx={{ ...segmentSx, flex: `0 0 ${COLS.operator}` }}>
          {condition.table && condition.field ? (
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: "0.875rem" }}>Operator</InputLabel>
              <Select
                size="small"
                value={condition.operator || ""}
                onChange={(e) =>
                  onUpdate({
                    ...condition,
                    operator: e.target.value,
                    value: "",
                  })
                }
                sx={{
                  minWidth: 0,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-select": {
                    py: 0.5,
                    fontSize: "0.875rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  },
                }}
              >
                {getOperatorOptions(condition.table, condition.field).map(
                  (option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          ) : (
            <FormControl size="small" disabled fullWidth>
              <InputLabel sx={{ color: "text.disabled" }}>Operator</InputLabel>
              <Select
                size="small"
                value=""
                sx={{
                  bgcolor: "transparent",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value="">Select field first</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ bgcolor: "divider", width: "1px" }}
        />

        <Box sx={{ ...segmentSx, flex: `0 0 ${COLS.value}` }}>
          {condition.table && condition.field && condition.operator ? (
            (() => {
              const valueFieldConfig = getValueFieldConfig(
                condition.table,
                condition.field,
                condition.operator
              );
              return valueFieldConfig ? (
                valueFieldConfig.type === "text" ? (
                  <TextField
                    size="small"
                    label="Value"
                    fullWidth
                    value={condition.value || ""}
                    onChange={(e) =>
                      onUpdate({ ...condition, value: e.target.value })
                    }
                    sx={{
                      minWidth: 0,
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiInputBase-input": {
                        fontSize: "0.85rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.85rem" },
                    }}
                  />
                ) : valueFieldConfig.type === "select" ? (
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: "0.875rem" }}>Value</InputLabel>
                    <Select
                      size="small"
                      value={condition.value || ""}
                      onChange={(e) =>
                        onUpdate({ ...condition, value: e.target.value })
                      }
                      sx={{
                        py: 0.25,
                        minWidth: 0,
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          py: 1,
                          fontSize: "0.875rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        },
                      }}
                    >
                      {valueFieldConfig.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  renderField(
                    valueFieldConfig,
                    condition.value,
                    (value) => onUpdate({ ...condition, value }),
                    true
                  )
                )
              ) : (
                <FormControl size="small" disabled fullWidth>
                  <InputLabel sx={{ color: "text.disabled" }}>Value</InputLabel>
                  <Select
                    size="small"
                    value=""
                    sx={{
                      bgcolor: "transparent",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="">Select operator first</MenuItem>
                  </Select>
                </FormControl>
              );
            })()
          ) : (
            <FormControl size="small" disabled fullWidth>
              <InputLabel sx={{ color: "text.disabled" }}>Value</InputLabel>
              <Select
                size="small"
                value=""
                sx={{
                  bgcolor: "transparent",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                <MenuItem value="">Select operator first</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ bgcolor: "divider", width: "1px" }}
        />

        <Box
          sx={{
            ...segmentSx,
            flex: `0 0 ${COLS.delete}`,
            justifyContent: "center",
            px: 0.5,
          }}
        >
          <IconButton
            onClick={onDelete}
            size="small"
            sx={{ color: "text.secondary", p: 0.5 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// Main PipeBuilder Component
export default function PipeBuilder({
  value,
  onChange,
  queryTypes,
  onJoinConfigChange,
  isTrigger,
}) {
  const { brandId } = useParams();
  const { handleGetDynamicTableFields, dynamicTableFields } = useAutomations();
  const [enhancedQueryTypes, setEnhancedQueryTypes] = useState(queryTypes);
  const [conditions, setConditions] = useState([]);
  const [nextId, setNextId] = useState(1);

  // NEW STATE: Join configurations
  const [dynamicJoinConfigs, setDynamicJoinConfigs] = useState({});

  // NEW: Define CRM_UserLeads fields that can be used for joining
  const leadsJoinableFields = [
    { value: "Phone", label: "Phone" },
    { value: "Email", label: "Email" },
  ];

  // Initialize conditions from value prop
  useEffect(() => {
    // Handle new structure: { conditions: [...], timeAmount: "5", timeInterval: "Minutes" }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      // New structure with conditions as a property
      if (Array.isArray(value.conditions)) {
        const conditionsWithIds = value.conditions.map((condition, index) => ({
          ...condition,
          id: condition.id || index + 1,
          logicalOperator:
            condition.logicalOperator || condition.operator || "AND",
        }));
        setConditions(conditionsWithIds);
        const maxId = Math.max(...conditionsWithIds.map((c) => c.id || 0));
        setNextId(maxId + 1);

        if (value.conditions[0]?.joinConfigs) {
          const initialJoinConfigs = value.conditions[0].joinConfigs || {};
          setDynamicJoinConfigs(initialJoinConfigs);
          if (onJoinConfigChange) onJoinConfigChange(initialJoinConfigs);
        }
      } else {
        setConditions([]);
        setNextId(1);
        setDynamicJoinConfigs({});
        if (onJoinConfigChange) onJoinConfigChange({});
      }
    } else if (Array.isArray(value) && value.length > 0) {
      // Legacy: value is directly the conditions array
      const conditionsWithIds = value.map((condition, index) => ({
        ...condition,
        id: condition.id || index + 1,
        logicalOperator:
          condition.logicalOperator || condition.operator || "AND",
      }));
      setConditions(conditionsWithIds);
      const maxId = Math.max(...conditionsWithIds.map((c) => c.id || 0));
      setNextId(maxId + 1);

      if (value[0]?.joinConfigs) {
        const initialJoinConfigs = value[0].joinConfigs || {};
        setDynamicJoinConfigs(initialJoinConfigs);
        if (onJoinConfigChange) onJoinConfigChange(initialJoinConfigs);
      }
    } else {
      setConditions([]);
      setNextId(1);
      setDynamicJoinConfigs({});
      if (onJoinConfigChange) onJoinConfigChange({});
    }
  }, [value]);

  useEffect(() => {
    if (brandId) {
      handleGetDynamicTableFields(brandId);
    }
  }, [brandId]);

  useEffect(() => {
    if (dynamicTableFields?.data) {
      const enhanced = {
        ...queryTypes,
        dynamicTables: dynamicTableFields.data,
      };
      setEnhancedQueryTypes(enhanced);
    } else {
      setEnhancedQueryTypes(queryTypes);
    }
  }, [queryTypes, dynamicTableFields]);

  // UPDATED: Generate SQL query with join configs
  const { sqlQuery, parameters } = useMemo(() => {
    if (conditions.length === 0) return { sqlQuery: "", parameters: [] };

    let paramIndex = 0;
    const params = [];

    const query = buildSqlQuery(
      conditions.map((c) => {
        const isRange = c.operator?.toLowerCase().includes("between");
        if (isRange && typeof c.value === "string" && c.value.includes("|")) {
          const [a, b] = c.value.split("|");
          params.push({ ParameterName: `@param${paramIndex++}`, Value: a });
          params.push({ ParameterName: `@param${paramIndex++}`, Value: b });
        } else {
          params.push({
            ParameterName: `@param${paramIndex++}`,
            Value: c.value,
          });
        }
        return c;
      }),
      "@BrandId",
      enhancedQueryTypes.dynamicTables || [],
      dynamicJoinConfigs // NEW: Pass join configs
    );

    return { sqlQuery: query, parameters: params };
  }, [conditions, enhancedQueryTypes.dynamicTables, dynamicJoinConfigs]); // Added dynamicJoinConfigs to dependencies

  const andConditions = conditions.filter((c) => c.logicalOperator === "AND");
  const orConditions = conditions.filter((c) => c.logicalOperator === "OR");

  const handleAddCondition = (logicalOperator) => {
    const newCondition = {
      id: nextId,
      table: "",
      field: "",
      operator: "",
      value: "",
      logicalOperator: logicalOperator,
      isWhitelist: true,
      joinConfigs: dynamicJoinConfigs,
    };

    const newConditions = [...conditions, newCondition];
    setConditions(newConditions);
    setNextId(nextId + 1);

    // Emit new structure with conditions array and time interval at same level
    const newValue = {
      conditions: newConditions,
      timeAmount: value?.timeAmount || "",
      timeInterval: value?.timeInterval || "Minutes",
    };
    onChange(newValue);
  };

  const handleUpdateCondition = (id, updatedCondition) => {
    const newConditions = conditions.map((condition) =>
      condition.id === id
        ? { ...updatedCondition, id, joinConfigs: dynamicJoinConfigs }
        : condition
    );
    setConditions(newConditions);

    // Emit new structure
    const newValue = {
      conditions: newConditions,
      timeAmount: value?.timeAmount || "",
      timeInterval: value?.timeInterval || "Minutes",
    };
    onChange(newValue);
  };

  const handleDeleteCondition = (id) => {
    const newConditions = conditions.filter((condition) => condition.id !== id);
    setConditions(newConditions);

    // Emit new structure
    const newValue = {
      conditions: newConditions,
      timeAmount: value?.timeAmount || "",
      timeInterval: value?.timeInterval || "Minutes",
    };
    onChange(newValue);
  };

  // Handle time interval changes for trigger nodes
  const handleTimeIntervalChange = (field, newValue) => {
    // Only emit if there are conditions, otherwise validation fails
    if (conditions.length === 0) {
      // Don't call onChange yet - wait until conditions are added
      return;
    }

    const newValueObj = {
      conditions: conditions,
      timeAmount: field === "timeAmount" ? newValue : value?.timeAmount || "",
      timeInterval:
        field === "timeInterval" ? newValue : value?.timeInterval || "Minutes",
    };
    onChange(newValueObj);
  };

  // Get current time interval from value prop (avoid state sync issues)
  const currentTimeInterval = useMemo(() => {
    if (!value || typeof value !== "object") {
      return { timeAmount: "", timeInterval: "Minutes" };
    }
    return {
      timeAmount: value.timeAmount || "",
      timeInterval: value.timeInterval || "Minutes",
    };
  }, [value]);

  const handleJoinConfigChange = (config) => {
    const newConfigs = {
      ...dynamicJoinConfigs,
      [config.tableName]: config,
    };
    setDynamicJoinConfigs(newConfigs);

    const updatedConditions = conditions.map((condition) => ({
      ...condition,
      joinConfigs: newConfigs,
    }));
    setConditions(updatedConditions);

    // Emit new structure
    const newValue = {
      conditions: updatedConditions,
      timeAmount: value?.timeAmount || "",
      timeInterval: value?.timeInterval || "Minutes",
    };
    onChange(newValue);

    // Notify parent component
    if (onJoinConfigChange) {
      onJoinConfigChange(newConfigs);
    }
  };

  const validConditionsCount = conditions.filter(
    (c) => c.table && c.field && c.operator && c.value
  ).length;

  const applyParamsToQuery = (query, params) => {
    if (!query || !params || params.length === 0) return query;

    let result = query;
    params.forEach((param) => {
      const regex = new RegExp(escapeRegExp(param.ParameterName), "g");
      result = result.replace(regex, toSqlLiteral(param.Value));
    });
    return result;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          overflowX: "hidden",
          overflowY: "auto",
          p: 2,
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {/* TIME INTERVAL SECTION - Only shown for trigger nodes */}
        {isTrigger && (
          <Box
            sx={{
              mb: 3,
              width: 680, // ← Match ConditionRow width
              flex: "0 0 auto",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: "medium", color: "primary.main" }}
              >
                Time Interval
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set how often this trigger should check for new leads matching
                the conditions below
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <FormControl size="small" sx={{ width: 150 }}>
                  <TextField
                    label="Amount"
                    type="number"
                    disabled={
                      andConditions.length === 0 && orConditions.length === 0
                    }
                    size="small"
                    value={currentTimeInterval.timeAmount}
                    onChange={(e) =>
                      handleTimeIntervalChange("timeAmount", e.target.value)
                    }
                    inputProps={{ min: 1 }}
                    sx={{ width: 150 }}
                  />
                </FormControl>
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={currentTimeInterval.timeInterval}
                    label="Unit"
                    disabled={
                      andConditions.length === 0 && orConditions.length === 0
                    }
                    onChange={(e) =>
                      handleTimeIntervalChange("timeInterval", e.target.value)
                    }
                  >
                    <MenuItem value="Minutes">Minutes</MenuItem>
                    <MenuItem value="Hours">Hours</MenuItem>
                    <MenuItem value="Days">Days</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 3, width: "100%", minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "medium" }}>
            ALL of these conditions
          </Typography>
          <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
            {andConditions.map((condition, index) => (
              <React.Fragment key={condition.id}>
                {index > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    AND
                  </Typography>
                )}
                <ConditionRow
                  condition={condition}
                  onUpdate={(updated) =>
                    handleUpdateCondition(condition.id, updated)
                  }
                  onDelete={() => handleDeleteCondition(condition.id)}
                  enhancedQueryTypes={enhancedQueryTypes}
                  dynamicJoinConfigs={dynamicJoinConfigs}
                  onJoinConfigChange={handleJoinConfigChange}
                  leadsJoinableFields={leadsJoinableFields}
                />
              </React.Fragment>
            ))}

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="outlined"
                size="small"
                sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                startIcon={<AddIcon color="textPrimary" />}
                onClick={() => handleAddCondition("AND")}
              >
                <Typography color="textPrimary">Add AND Condition</Typography>
              </Button>

              {andConditions.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => {
                    const newConditions = conditions.filter(
                      (c) => c.logicalOperator !== "AND"
                    );
                    setConditions(newConditions);
                    onChange(newConditions);
                  }}
                  sx={{ textTransform: "none", minWidth: "auto" }}
                  startIcon={<DeleteIcon />}
                >
                  Delete All AND
                </Button>
              )}
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3, width: "100%", minWidth: 0 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "medium" }}>
            And ANY of these conditions
          </Typography>
          <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
            {orConditions.map((condition, index) => (
              <React.Fragment key={condition.id}>
                {index > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="center"
                  >
                    OR
                  </Typography>
                )}
                <ConditionRow
                  condition={condition}
                  onUpdate={(updated) =>
                    handleUpdateCondition(condition.id, updated)
                  }
                  onDelete={() => handleDeleteCondition(condition.id)}
                  enhancedQueryTypes={enhancedQueryTypes}
                  dynamicJoinConfigs={dynamicJoinConfigs}
                  onJoinConfigChange={handleJoinConfigChange}
                  leadsJoinableFields={leadsJoinableFields}
                />
              </React.Fragment>
            ))}

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="outlined"
                size="small"
                sx={{ whiteSpace: "nowrap", textTransform: "none" }}
                startIcon={<AddIcon color="textPrimary" />}
                onClick={() => handleAddCondition("OR")}
              >
                <Typography color="textPrimary">Add OR Condition</Typography>
              </Button>

              {orConditions.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => {
                    const newConditions = conditions.filter(
                      (c) => c.logicalOperator !== "OR"
                    );
                    setConditions(newConditions);
                    onChange(newConditions);
                  }}
                  sx={{ textTransform: "none", minWidth: "auto" }}
                  startIcon={<DeleteIcon />}
                >
                  Delete All OR
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          maxHeight: "40vh",
          overflow: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: "background.paper",
            width: "710px",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
            <Typography variant="h6" gutterBottom>
              Generated SQL Query
            </Typography>
            {validConditionsCount > 0 && (
              <Chip
                label={`${validConditionsCount} condition${
                  validConditionsCount !== 1 ? "s" : ""
                }`}
                size="small"
                color="primary"
              />
            )}
          </Box>

          {sqlQuery ? (
            <>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: "grey.900",
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflow: "auto",
                  maxHeight: "300px",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    color: "#4fc3f7",
                    margin: 0,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                  }}
                >
                  {applyParamsToQuery(sqlQuery, parameters)}
                </Typography>
              </Paper>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  This query will filter leads based on the conditions you've
                  specified. Parameters (@param0, @param1, etc.) are replaced
                  with actual values when executed.
                </Typography>
              </Box>

              {validConditionsCount > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Query Summary:
                  </Typography>
                  <Typography variant="caption" component="div">
                    {andConditions.filter((c) => c.table && c.field).length >
                      0 && (
                      <Box>
                        • AND conditions:{" "}
                        {andConditions
                          .filter((c) => c.table && c.field)
                          .map(
                            (c) =>
                              `${c.table}.${c.field} ${c.operator} ${
                                c.operator?.toLowerCase().includes("between") &&
                                c.value?.includes("|")
                                  ? c.value.split("|").join(" - ")
                                  : c.value
                              }`
                          )
                          .join(", ")}
                      </Box>
                    )}
                    {orConditions.filter((c) => c.table && c.field).length >
                      0 && (
                      <Box>
                        • OR conditions:{" "}
                        {orConditions
                          .filter((c) => c.table && c.field)
                          .map(
                            (c) =>
                              `${c.table}.${c.field} ${c.operator} ${
                                c.operator?.toLowerCase().includes("between") &&
                                c.value?.includes("|")
                                  ? c.value.split("|").join(" - ")
                                  : c.value
                              }`
                          )
                          .join(", ")}
                      </Box>
                    )}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "150px",
                backgroundColor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="textSecondary" align="center">
                Add conditions to generate a SQL query.
                <br />
                The query will appear here as you build your conditions.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
